import React, { useCallback, useContext, useMemo, useState } from "react";
import { FormControlerContext } from "../form-controler-context/form-controler-context";
import { getDataUrl } from "./helpers";
import { useSnackbar } from "notistack";
import action from "components/UI/buttons/NotificationDismissButton";
import {
  formName,
  fieldConfig,
  editDataFieldConfig,
  editDataFormName,
} from "./review-config";
import AuthContext from "store/auth-context/auth-context";
import { axiosGu } from "config";

export const ReviewContext = React.createContext({});

export function ReviewContextProvider({ children }) {
  const { enqueueSnackbar } = useSnackbar();

  const {
    fieldsState: { allValues, allConfigs, allErrors },
    controlers: {
      resetForm,
      removeFormState,
      getFormCofig,
      defaultFieldOptions,
      setFieldValue,
      setFieldError,
      setFieldTouched,
    },
  } = useContext(FormControlerContext);

  const { user } = useContext(AuthContext);

  const changeHandler = useCallback(
    function (e, selectedValue) {
      const { fieldName, error } = this;

      let enteredValue;
      if (fieldName === "fromDate" || fieldName === "toDate") {
        enteredValue = e.toISOString().substring(0, 10);
      } else {
        enteredValue =
          selectedValue || selectedValue === null
            ? selectedValue
            : e?.target?.value;
      }

      setFieldValue(formName, fieldName, enteredValue);

      enteredValue !== "" && error && setFieldError(formName, fieldName, false);
    },
    [setFieldError, setFieldValue]
  );
  const filterFormDefaultFieldOptions = useMemo(
    () => defaultFieldOptions.bind({ changeHandler }, formName),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultFieldOptions]
  );

  const editChangeHandler = useCallback(
    function (e, selectedValue) {
      const enteredValue =
        selectedValue || selectedValue === null
          ? selectedValue
          : e?.target?.value;
      const { formName, fieldName, error } = this;

      setFieldValue(formName, fieldName, enteredValue);

      if (fieldName === editDataFieldConfig.division.fieldName) {
        setFieldValue(
          editDataFormName,
          editDataFieldConfig.desk.fieldName,
          null
        );
        setFieldError(editDataFormName, editDataFieldConfig.desk.fieldName);
      }

      enteredValue !== "" && error && setFieldError(formName, fieldName, false);
    },
    [setFieldValue, setFieldError]
  );

  const editDataDefaultFieldOptions = useMemo(
    () =>
      defaultFieldOptions.bind(
        { changeHandler: editChangeHandler },
        editDataFormName
      ),
    [defaultFieldOptions, editChangeHandler]
  );

  const { [formName]: values = {}, [editDataFormName]: editDataValues = {} } =
    allValues;

  const {
    [formName]: formConfig = {},
    [editDataFormName]: editDataConfig = {},
  } = allConfigs;

  const { [editDataFormName]: editDataErrors = {} } = allErrors;

  const [loadingData, setLoadingData] = useState({
    inProgress: false,
    finished: false,
    error: false,
  });
  const [data, setData] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState();
  const [tableRowSelectionModel, setTableRowSelectionModel] = useState();
  const getTableRowSelectionModel = (selectedTable) => {
    setTableRowSelectionModel(selectedTable);
  };

  const [activePage, setActivePage] = useState(0);
  const [sendingStatus, setSendingStatus] = useState(false);

  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => {
    setOpen(newOpen);
  };
  const getData = async ({ pageNum, approvedOption, firstSearch }) => {
    try {
      setLoadingData({
        inProgress: true,
        finished: false,
        error: false,
      });

      const response = await axiosGu.get(
        getDataUrl(values, pageNum, approvedOption)
      );
      const data = await response.data;

      if (firstSearch) {
        setData(data.greatTeleperenceDatas);
      } else {
        setData((prevState) => [...prevState, ...data.greatTeleperenceDatas]);
      }
      setPaginationInfo({
        pageNumber: data.pageNumber || "1",
        elementsInPage: data.elementsInPage,
        totalPages: data.totalPages,
        totalElements: data.totalElements,
        maxElementsPerPage: data.maxElementsPerPage,
      });

      setLoadingData({
        inProgress: false,
        finished: true,
        error: false,
      });
    } catch (err) {
      const resStatus = err?.response?.status;
      if (resStatus === 400) {
        enqueueSnackbar("No data to load!");
      } else if (resStatus !== 401) {
        enqueueSnackbar(`Fetching review data failed! ${err}`, {
          variant: "error",
          action,
        });
      }
      setLoadingData({
        inProgress: false,
        finished: true,
        error: err.message,
      });
    }
  };

  const pageChangeHandler = useCallback(
    function (page) {
      if (page === "forward" && activePage < data.length - 1) {
        setActivePage((state) => {
          return ++state;
        });
      }

      if (page === "back" && activePage > 0) {
        setActivePage((state) => {
          return --state;
        });
      }
    },
    [data.length, activePage]
  );

  const pageChangeKeyDownHandler = useCallback(
    function (e) {
      if (e.target.tagName === "INPUT") return;

      if (e.keyCode === 37) pageChangeHandler("back");
      if (e.keyCode === 39) pageChangeHandler("forward");
    },
    [pageChangeHandler]
  );
  const approveDenieHandler = useCallback(
    async function (approved) {
      if (data[activePage].approved === approved) return;
      try {
        setSendingStatus(true);
        const approvedBy = { name: user.username, id: user.id };
        const dataToSubmit = {
          id: data[activePage].id,
          approvedBy,
          approved,
          denialReason: data[activePage]?.denialReason,
        };

        await axiosGu.post(`/great-telepresence/review/submit`, dataToSubmit);

        setData((state) => {
          const newState = [...state];
          newState[activePage] = {
            ...newState[activePage],
            approved,
            approvedBy,
            modified: Date.now(),
          };
          return newState;
        });

        setTimeout(() => {
          setActivePage((state) =>
            data.length > state + 1 ? state + 1 : state
          );
        }, 500);
      } catch (err) {
        const resStatus = err?.response?.status;
        if (resStatus === 401) {
          enqueueSnackbar(`Submitting status failed`, {
            variant: "error",
            action,
          });
        }
      }
      setSendingStatus(false);
    },
    [activePage, data, user, enqueueSnackbar]
  );

  const approveDenieKeyDownHandler = useCallback(
    function (e) {
      if (e.target.tagName === "INPUT") return;
      if (e.keyCode === 65) approveDenieHandler(true);
      if (e.keyCode === 68) approveDenieHandler(false);
    },
    [approveDenieHandler]
  );

  return (
    <ReviewContext.Provider
      value={{
        fieldControlers: {
          defaultFieldOptions: filterFormDefaultFieldOptions,
          fieldConfig,
          editDataDefaultFieldOptions,
          editDataFieldConfig,
          setFieldValue,
          setFieldError,
          setFieldTouched,
        },

        formControlers: {
          resetFilters: resetForm,
          removeFormState,
          getFormCofig,
        },

        actions: {
          getData,
          approveDenieHandler,
          approveDenieKeyDownHandler,
          pageChangeKeyDownHandler,
          pageChangeHandler,
          setActivePage,
          getTableRowSelectionModel,
        },

        fieldsState: { values },
        formConfig,
        formName,
        editDataFormName,
        editDataConfig,
        loadingData,
        sendingStatus,
        data,
        setData,
        tableRowSelectionModel,
        paginationInfo,
        setPaginationInfo,
        open,
        toggleDrawer,
        activePage,
        editDataValues,
        editDataErrors,
      }}
    >
      {children}
    </ReviewContext.Provider>
  );
}
